import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { PropertyReportRecord } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import {
	PropertyReportDialogComponent,
	PropertyReportFilterOptions,
	PropertyReportFilters,
} from '../property-report-dialog/property-report-dialog.component';
import { UserProfileService } from '../../../services/user-profile.service';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { NgClass } from '@angular/common';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-property-report-table',
	templateUrl: './property-report-table.component.html',
	styleUrls: ['./property-report-table.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		DataSetComponent_1,
		BreadcrumbLabelDirective,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		NgClass,
		DataSetLoadingRowComponent,
	],
})
export class PropertyReportTableComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns: string[] = [
		'deviceSerialNumber',
		'deviceStatus',
		'propertyName',
		'deviceLocation',
		'postalCode',
	];
	dataSource = new MatTableDataSource<PropertyReportRecord>();
	private filterSubscription?: Subscription;
	isCareCompany = false;

	reportFilters: PropertyReportFilters;
	compiledFilters: QueryParam[] = [];
	hasFilters = false;
	filterOptions?: PropertyReportFilterOptions;

	breadcrumbLabel = $localize`:@@propertyReportTable-breadcrumbLabel:Property Report`;

	generateAccessReportLoadingKey = ApiLoadingKeys.generateAccessReport;
	generatePropertyReportCsvLoadingKey =
		ApiLoadingKeys.generatePropertyReportCsv;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService
	) {
		this.reportFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.reportFilters;
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
		this.hasFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.hasFilters;
		this.filterOptions =
			this.router.lastSuccessfulNavigation?.extras?.state?.filterOptions;
	}

	ngAfterViewInit(): void {
		this.dataSet?.applyFilters();
		this.filterSubscription = this.dataSet?.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.generatePropertyReport([
						...filter,
						...(this.compiledFilters ?? []),
					]);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});

		this.checkCareCompany()
			.then(() => {
				if (this.isCareCompany) {
					this.displayedColumns.splice(2, 0, 'clientName', 'clientIdn');
				}
			})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.isCareCompany = companyData?.company?.companyType === 'care';
		}
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	getStatuses(status: string): string {
		if (status === 'inService') {
			return 'In Service';
		}
		return status.charAt(0).toUpperCase() + status.slice(1);
	}

	onRefineFiltersClicked(): void {
		this.dialog
			.open(PropertyReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					reportFilters: this.reportFilters,
					filterOptions: this.filterOptions,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.applyFilters();
			});
	}

	async onDownloadClicked(): Promise<void> {
		const fileName = this.hasFilters
			? 'filteredPropertyReport'
			: 'propertyReport';
		await this.apiService.generatePropertyReportCsv(
			fileName,
			this.compiledFilters
		);
	}

	rowId(row: PropertyReportRecord): string {
		return row?.id || 'unknown-id';
	}
}
